import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  Typography,
  ExpandMoreIcon,
  AddIcon,
  DeleteIcon,
  GridRowSelectionModel,
} from "../../../../shared/utils/muiImports";
import StaffForm from "../components/StaffForm";
import StaffList from "../components/StaffList";
import useStaffs from "../../../../hooks/staff/useStaff";
import useStaffForm from "../../../../hooks/staff/useStaffForm";
import { Staff } from "../Staff.type";
import { toast } from "react-toastify";
import DeleteConfirmationDialog from "../../../../shared/components/DeleteConfirmationDialog";

const StaffPage: React.FC = () => {
  const emptyStaff: Staff = {
    id: 0,
    userName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    gender: "",
    address: "",
    dateOfBirth: "",
    email: "",
    isActive: true,
    createdDateTime: "",
    modifiedDateTime: null,
    modifiedUserId: 0,
    passwordHash: "",
    rowVersion: "",
  };
  const StaffListHeader: string = "StaffList";
  const StaffFormHeader: string = "StaffForm";

  const [staffListTitle] = useState("List of users");
  const [staffFormTitle, setstaffFormTitle] = useState("User");

  const [isStaffListExpanded, setIsStaffListExpanded] = useState(true);
  const [isStaffFormExpanded, setIsStaffFormExpanded] = useState(false);
  const { staffs, editStaff, createStaff, removeStaff } = useStaffs();
  const {
    handleRowClick,
    setCurrentStaff,
    currentStaff,
    handleInputChange,
    handleSave,
    errors,
    setErrors,
  } = useStaffForm(emptyStaff);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    []
  );

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectionModel(newSelection);
  };

  const handleCreateNewstaff = () => {
    setstaffFormTitle("Create a user");
    setCurrentStaff(emptyStaff);
    manageAccordion(true);
  };

  const manageAccordion = (isExpanded: boolean) => {
    setIsStaffFormExpanded(isExpanded);
    setIsStaffListExpanded(!isExpanded);
  };

  const HandleSaveClick = () => {
    handleSave((staff: Staff) => {
      if (staff.id) {
        editStaff(currentStaff)
          .then(() => {
            toast.success("Staff updated successfully!");
            manageAccordion(false);
          })
          .catch(() => {
            toast.error("Failed to update Staff");
            manageAccordion(true);
          });
      } else {
        if (!currentStaff.createdDateTime) {
          currentStaff.createdDateTime = new Date().toISOString();
        }
        createStaff(currentStaff)
          .then(() => {
            toast.success("Staff added successfully!");
            manageAccordion(false);
          })
          .catch(() => {
            toast.error("Failed to add Staff");
            manageAccordion(true);
          });
      }
      handleCreateNewstaff();
    });
  };

  const handleDeleteRequest = () => {
    if (!selectionModel) {
      toast.error("No staff selected. Please select a staff from the grid.");
      return;
    }

    setDeleteId(Number(selectionModel[0]));
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteId(0);
    setIsDeleteDialogOpen(false);
  };

  const handleDelete = () => {
    removeStaff(deleteId)
      .then(() => toast.success("staff deleted successfully!"))
      .catch(() => toast.error("Failed to delete Staff"));
    setIsDeleteDialogOpen(false);
    handleCreateNewstaff();
    manageAccordion(false);
  };

  const handleRowSelect = (e: any) => {
    setstaffFormTitle("Update user");
    manageAccordion(true);
    handleRowClick(e);
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      if (panel === StaffListHeader) {
        manageAccordion(false);
      }

      if (panel === StaffFormHeader) {
        manageAccordion(true);
      }
    };

  const handleCancel = (e: any) => {
    setIsStaffFormExpanded(false);
    setIsStaffListExpanded(true);
    setCurrentStaff(emptyStaff);
    setErrors({});
    setstaffFormTitle("User");
  };

  return (
    <Box sx={{ padding: 0 }}>
      <Grid item xs={12} sx={{ padding: 1 }}>
        <Accordion
          expanded={isStaffListExpanded}
          onChange={handleAccordionChange(StaffListHeader)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="staff-list-content"
            id="staff-list-header"
          >
            <Typography>{staffListTitle}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <StaffList
              staffs={staffs}
              onStaffSelect={handleRowSelect}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={handleSelectionChange}
            />
            <Grid item xs={12} sx={{ pt: 2 }}>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateNewstaff}
              >
                Add
              </Button>

              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteRequest}
              >
                Delete
              </Button>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12} sx={{ padding: 1 }}>
        <Accordion
          expanded={isStaffFormExpanded}
          onChange={handleAccordionChange(StaffFormHeader)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="staff-form-content"
            id="staff-form-header"
          >
            <Typography>{staffFormTitle}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <StaffForm
              staff={currentStaff}
              errors={errors}
              onChange={handleInputChange}
              onSave={HandleSaveClick}
              onCancel={handleCancel}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>

      <DeleteConfirmationDialog
        message={`Are you sure you want to delete this Staff?`} // Pass the message here
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDelete}
        entityName={"Staff"}
      ></DeleteConfirmationDialog>
    </Box>
  );
};

export default StaffPage;
